import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Tabs, Tab } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import SourceIcon from '@mui/icons-material/Source';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Chip from '@mui/material/Chip';
import fetchData from '../../utils/fetchData';
import { useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const SampleNextArrow = props => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: 'block', right: 10, zIndex: 1 }}
      onClick={onClick}
    />
  );
};

const SamplePrevArrow = props => {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: 'block', left: 10, zIndex: 1 }}
      onClick={onClick}
    />
  );
};

export default function NaicsInfo () {
  const [responseData, setResponseData] = useState([]);
  const [selectedTab, setSelectedTab] = useState({}); // Track selected tab for each NAICS
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );

  useEffect(() => {
    if (selectedApplicationId) {
      fetchData(
        `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/customer_naics_cmi/?application_id=${selectedApplicationId}`,
        'GET'
      ).then(data => {
        // Duplicate the data for testing
        const duplicatedData = [...data];
        setResponseData(duplicatedData);
        const initialTabs = data?.reduce((acc, naicsItem, index) => {
          acc[index] = 0; // Default to the first tab for each NAICS
          return acc;
        }, {});
        setSelectedTab(initialTabs);
      });
    }
  }, [selectedApplicationId]);

  const handleTabChange = (naicsIndex, event, newValue) => {
    setSelectedTab(prev => ({
      ...prev,
      [naicsIndex]: newValue
    }));
  };

  // Settings for react-slick carousel with custom arrows
  const carouselSettings = {
    dots: true,
    arrows: true,
    infinite: false,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1
        }
      }
    ]
  };

  return (
    <Box className='p-0'>
      {responseData.length > 0 && (
        <h3 className='text-left mb-5 pb-3 text-xl text-orange-600'>
          Classification
        </h3>
      )}

      {/* Carousel container using react-slick */}
      <Slider {...carouselSettings}>
        {responseData.map((naicsItem, naicsIndex) => (
          <Box
            key={naicsIndex}
            sx={{
              padding: '0 20px',
              // Force a minimum width for each slide
              minWidth: '220px',
              minHeight: '200px',
              maxHeight: '250px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Card
              className='shadow-lg'
              sx={{
                minHeight: '300px',
                width: '100%',
                backgroundColor:
                  naicsItem.source === 'llm' ? '#FFF0ED' : '#eff6ff',
                borderRadius: '15px',
                padding: '10px',
                // Enable relative positioning on AI cards for overlay
                position: naicsItem.source === 'llm' ? 'relative' : 'static'
              }}
            >
              <CardContent>
                {/* Conditionally render AI Generated chip */}
                {naicsItem.source === 'llm' && (
                  <Chip
                    icon={<AutoAwesomeIcon style={{ color: '#ea580c' }} />}
                    label='AI Generated'
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: '#fff',
                      border: '1px solid #ea580c'
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '14px',
                    marginBottom: '8px'
                  }}
                >
                  NAICS:{' '}
                  <span
                    style={{
                      fontWeight: 'normal',
                      fontSize: '14px'
                    }}
                  >
                    {naicsItem.naics}
                  </span>
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '14px',
                    marginBottom: '8px',
                    minHeight: '52px',
                    maxHeight: '60px',
                    overflow: 'auto'
                  }}
                >
                  Description:{' '}
                  <span
                    style={{
                      fontWeight: 'normal',
                      fontSize: '14px',
                      display: 'block',
                      maxHeight: '75px',
                      overflow: 'auto'
                    }}
                  >
                    {naicsItem.naics_description || '---'}
                  </span>
                </Typography>

                <Box className='mt-4'>
                  <Tabs
                    value={selectedTab[naicsIndex] || 0}
                    onChange={(event, newValue) =>
                      handleTabChange(naicsIndex, event, newValue)
                    }
                    indicatorColor='primary'
                    textColor='primary'
                    variant='scrollable'
                    scrollButtons='auto'
                    className='appetite-tabs'
                    sx={{
                      marginLeft: naicsItem.cmis?.length > 2 ? '-25px' : '0'
                    }}
                  >
                    {naicsItem.cmis?.map((cmiItem, cmiIndex) => (
                      <Tab
                        key={cmiIndex}
                        icon={
                          cmiItem.selected_cmi ? (
                            <DescriptionIcon sx={{ color: '#4CAF50' }} />
                          ) : (
                            <DescriptionIcon />
                          )
                        }
                        className='tabs-list'
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            {`CMI ${cmiItem.cmi}`}
                            {cmiItem.selected_cmi && (
                              <CheckBoxIcon sx={{ color: '#4CAF50', ml: 1 }} />
                            )}
                          </Box>
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: cmiItem.selected_cmi ? 'bold' : 'normal',
                          fontSize: '14px',
                          backgroundColor: cmiItem.selected_cmi
                            ? '#E8F5E9'
                            : 'transparent',
                          borderRadius: '4px 4px 0 0',
                          minWidth: '220px',
                          minHeight: '55px'
                        }}
                      />
                    ))}
                  </Tabs>

                  {/* Content for Selected Tab */}
                  <Box
                    className='p-4'
                    sx={{
                      minHeight: '150px',
                      overflowY: 'auto',
                      transition: 'height 0.3s ease-in-out',
                      borderRadius: '0',
                      backgroundColor: '#fff'
                    }}
                  >
                    {naicsItem.cmis?.map(
                      (cmiItem, cmiIndex) =>
                        selectedTab[naicsIndex] === cmiIndex && (
                          <Box key={cmiIndex}>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '14px',
                                marginBottom: '8px'
                              }}
                            >
                              CMI:{' '}
                              <span
                                style={{
                                  fontWeight: 'normal',
                                  fontSize: '14px'
                                }}
                              >
                                {cmiItem.cmi}
                              </span>
                              {cmiItem.is_selected && (
                                <>
                                  {' '}
                                  {cmiItem.is_inferred_cmi ? (
                                    <Tooltip
                                      title={
                                        <span
                                          style={{
                                            fontSize: '12px',
                                            color: '#fff'
                                          }}
                                        >
                                          Inferred by AI
                                        </span>
                                      }
                                      arrow
                                      placement='top'
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            backgroundColor: '#4CAF50',
                                            borderRadius: '4px',
                                            padding: '6px 8px'
                                          }
                                        }
                                      }}
                                    >
                                      <OnlinePredictionIcon
                                        style={{
                                          marginLeft: '8px',
                                          color: '#4CAF50',
                                          verticalAlign: 'middle',
                                          cursor: 'pointer',
                                          float: 'right'
                                        }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip
                                      title={
                                        <span
                                          style={{
                                            fontSize: '12px',
                                            color: '#fff'
                                          }}
                                        >
                                          Extracted from Application
                                        </span>
                                      }
                                      arrow
                                      placement='top'
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            backgroundColor: '#4CAF50',
                                            borderRadius: '4px',
                                            padding: '6px 8px'
                                          }
                                        }
                                      }}
                                    >
                                      <SourceIcon
                                        style={{
                                          marginLeft: '8px',
                                          color: '#4CAF50',
                                          verticalAlign: 'middle',
                                          cursor: 'pointer'
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '14px',
                                marginBottom: '8px'
                              }}
                            >
                              Description:{' '}
                              <span
                                style={{
                                  fontWeight: 'normal',
                                  fontSize: '12px'
                                }}
                              >
                                {cmiItem.cmi_description || '---'}
                              </span>
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '14px',
                                marginBottom: '8px'
                              }}
                            >
                              Appetite:{' '}
                              <span
                                style={{
                                  fontWeight: 'normal',
                                  fontSize: '14px'
                                }}
                              >
                                {cmiItem.appetite_decision || '---'}
                              </span>
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                fontSize: '14px',
                                marginBottom: '8px'
                              }}
                            >
                              ISOGL:{' '}
                              <span
                                style={{
                                  fontWeight: 'normal',
                                  fontSize: '14px'
                                }}
                              >
                                {cmiItem.iso || '---'}
                              </span>
                            </Typography>
                            <Box className='mt-2'>
                              <Typography
                                sx={{
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                  marginBottom: '8px'
                                }}
                              >
                                Hazard Levels:
                              </Typography>
                              <Box className='text-gray-600'>
                                {Object.entries(cmiItem?.lobs).map(
                                  ([lob, level], i) => (
                                    <Typography
                                      key={i}
                                      sx={{
                                        fontSize: '14px',
                                        marginBottom: '5px',
                                        marginLeft: '20px'
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 'bold',
                                          fontSize: '14px'
                                        }}
                                      >
                                        {lob}:
                                      </span>{' '}
                                      {level || '--'}
                                    </Typography>
                                  )
                                ) || (
                                  <Typography
                                    sx={{
                                      fontWeight: 'normal',
                                      fontSize: '14px'
                                    }}
                                  >
                                    ---
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        )
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Slider>
    </Box>
  );
}
